// ECMJ 프로그램 아이디 : REQ_대한항공 적립몰_01_01 (jg26.kim)
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import PropTypes from 'prop-types';
import { useTheme } from 'styled-components';

import clsx from 'clsx';

import useScrollDirection from '@ecp/common/src/hooks/useScrollDirection';
import useLoginForward from '@ecp/common/src/hooks/useLoginForward';
import { FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import { TextBox } from '@ecp/common/src/text/TextBox';

import { searchKeywordState } from '@mo-recoil/search/atom';
import { userInfoState } from '@mo-recoil/common/user/atom';
import { useConfirmDialog } from '@mo-hooks/common/useModal';
import useSiteInfo from '@mo-hooks/common/useSiteInfo';
import { ReactComponent as HomeIcon } from '@mo-assets/icon/KoreanAirSavings/icon_home.svg';
import { ReactComponent as CustomerServiceIcon } from '@mo-assets/icon/KoreanAirSavings/icon_inquiry.svg';
import { ReactComponent as SearchIcon } from '@mo-assets/icon/KoreanAirSavings/icon_search.svg';
import { ReactComponent as MyPageIcon } from '@mo-assets/icon/KoreanAirSavings/icon_mypage.svg';
import {
  Container,
  MenuButtonContainer,
  Menu,
  MenuWrapper,
} from '@ecp/mo/sites/koreanairSavings/styles/navigation/KoreanAirSavingsNavigationMenuStyles';

function KoreanAirSavingsNavigationMenuItem({ iconType: Icon, selected, text, width, height }) {
  const theme = useTheme();

  const color = useMemo(
    () => ({
      icon: selected ? theme.color.icon.current : theme.color.icon.normal,
      label: selected ? theme.color.primary : theme.color.text.guide,
    }),
    [selected, theme.color.icon, theme.color.primary, theme.color.text.guide]
  );

  const fontWeight = useMemo(() => (selected ? 'bold' : 'lighter'), [selected]);

  return (
    <FlexBoxColumn>
      <Icon fill={color.icon} width={width} height={height} selected={selected} />
      <TextBox color={color.label} selected={selected} font-size={10} font-weight={fontWeight}>
        {text}
      </TextBox>
    </FlexBoxColumn>
  );
}

KoreanAirSavingsNavigationMenuItem.propTypes = {
  iconType: PropTypes.elementType,
  selected: PropTypes.bool,
  text: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
};

export default function KoreanAirSavingsNavigationMenu() {
  const resetSearchKeyword = useResetRecoilState(searchKeywordState);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { login } = useRecoilValue(userInfoState);
  const { siteNumber } = useSiteInfo();
  const scrollDirection = useScrollDirection({ siteNumber: siteNumber });
  const scrollDirectionStyle = clsx(scrollDirection);
  const { showConfirmDialog } = useConfirmDialog();
  const loginForward = useLoginForward(siteNumber, showConfirmDialog);

  const isHome = useMemo(() => pathname === '/', [pathname]);

  const navigateSearch = (e) => {
    e.stopPropagation();
    e.preventDefault();

    resetSearchKeyword();
    navigate('/search');
  };

  const handleMoveClick = (to) => {
    if (!login) {
      loginForward();
    } else {
      navigate(`${to}`);
    }
  };

  const menuPath = window.location.pathname.replace('/', '');

  return (
    <Container className={scrollDirectionStyle}>
      <MenuButtonContainer>
        {!isHome && (
          <Menu onClick={() => navigate('/')} data-ds-label2={'navi_home'}>
            <MenuWrapper>
              <KoreanAirSavingsNavigationMenuItem
                iconType={HomeIcon}
                width={'23px'}
                height={'20px'}
                selected={menuPath === ''}
                text={'홈'}
              />
            </MenuWrapper>
          </Menu>
        )}
        <Menu onClick={() => handleMoveClick('/my-page')} data-ds-label2={'navi_my_page'}>
          <KoreanAirSavingsNavigationMenuItem
            iconType={MyPageIcon}
            width={'17px'}
            height={'20px'}
            selected={menuPath === 'my-page'}
            text={'마이페이지'}
          />
        </Menu>
        <Menu onClick={navigateSearch} data-ds-label2={'navi_search'}>
          <KoreanAirSavingsNavigationMenuItem
            iconType={SearchIcon}
            width={'20px'}
            height={'20px'}
            selected={menuPath === 'search'}
            text={'검색'}
          />
        </Menu>
        <Menu
          onClick={() =>
            navigate(login ? '/customer-service/qna' : login ? '/customer-service/qna' : '/customer-service/notice')
          }
          data-ds-label2={'navi_customer_service'}
        >
          <KoreanAirSavingsNavigationMenuItem
            iconType={CustomerServiceIcon}
            width={'18px'}
            height={'20px'}
            selected={menuPath === 'customer-service'}
            text={'고객센터'}
          />
        </Menu>
      </MenuButtonContainer>
    </Container>
  );
}

